import React, { useState, useRef, useEffect } from 'react';
import { Upload, FileText, AlertCircle, ArrowLeft, X, Eye, Edit3, ChevronDown, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { BillData, UserType } from '../types';
import { analyzeBillWithOCR, type BillInfo } from '../OCR/index.ts';
import { MonthlyConsumptionInput } from './MonthlyConsumptionInput';
import { normalizeMorningConsumptionPct } from '../utils/consumptionNormalization';
import { BILL_OCR_ENABLED } from '../constants/featureFlags';

interface BillUploadSimpleProps {
  onNext: (data: BillData) => void;
  onBack?: () => void;
  userType: UserType | null;
  initialData?: BillData | null;
  currentProjectId?: string | null;
  user?: any;
}

export function BillUploadSimple({ onNext, onBack, initialData }: BillUploadSimpleProps) {
  const [billData, setBillData] = useState<BillData>({
    type: 'bill',
    monthlyBill: 0,
    utilityProvider: '',
    yearlyConsumption: 0,
    // Residential workflow: monthlyConsumptionArray is the source of truth for usage
    monthlyConsumptionArray: initialData?.monthlyConsumptionArray?.length === 12
      ? initialData.monthlyConsumptionArray
      : new Array(12).fill(0),
    locationPhotos: []
  });

  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  
  // OCR-related state
  const [isProcessingOCR, setIsProcessingOCR] = useState(false);
  const [extractedBillInfo, setExtractedBillInfo] = useState<BillInfo | null>(null);
  const [showOCRPreview, setShowOCRPreview] = useState(false);

  // Utility provider dropdown state
  const [showUtilityDropdown, setShowUtilityDropdown] = useState(false);
  
  // Validation state
  const [validationErrors, setValidationErrors] = useState<{
    monthlyUsage?: string;
    monthlyBill?: string;
    utilityProvider?: string;
    morningConsumptionPct?: string;
    customTariffSarPerKwh?: string;
  }>({});
  const [manualEntry, setManualEntry] = useState(false);

  // Track which fields were filled by OCR
  const [ocrFilledFields, setOcrFilledFields] = useState<{
    monthlyUsage?: boolean;
    monthlyBill?: boolean;
  }>({});

  // Utility providers list
  const utilityProviders = [
    'Saudi Electricity Company (SEC)',
    'National Grid SA',
    'Microgrid (Multiple System, like PV diesel)',
  ];

  // Initialize state from initialData
  useEffect(() => {
    if (initialData) {
      setBillData(initialData);
    }
  }, [initialData]);

  // Track selected files
  const [selectedFiles, setSelectedFiles] = useState<{
    bill?: File;
  }>({});

  // Validation functions
  const validateMonthlyConsumptionArray = (values: number[] | undefined) => {
    if (!values || values.length !== 12) {
      return 'Please enter your monthly consumption for all 12 months';
    }
    const total = values.reduce((sum, v) => sum + (Number.isFinite(v) ? v : 0), 0);
    if (total <= 0) {
      return 'Total annual consumption must be greater than 0';
    }
    const hasInvalid = values.some(v => !Number.isFinite(v) || v < 0 || v > 100000);
    if (hasInvalid) {
      return 'Please check your monthly consumption values (0–100,000 kWh)';
    }
    return '';
  };

  const validateMonthlyBill = (value: number) => {
    if (value <= 0) {
      return 'Monthly bill amount must be greater than 0';
    }
    if (value > 100000) {
      return 'Monthly bill amount seems too high. Please verify the value';
    }
    return '';
  };

  const validateUtilityProvider = (value: string) => {
    if (!value.trim()) {
      return 'Please select a utility provider';
    }
    return '';
  };

  const validateMorningConsumptionPct = (value: number | undefined) => {
    if (value == null) return '';
    if (!Number.isFinite(value)) return 'Morning consumption percentage must be a number';
    if (value < 0 || value > 100) return 'Morning consumption percentage must be between 0 and 100';
    return '';
  };

  const validateCustomTariffSarPerKwh = (value: number | undefined) => {
    if (value == null) return '';
    if (!Number.isFinite(value)) return 'Custom tariff must be a number';
    if (value <= 0) return 'Custom tariff must be greater than 0';
    if (value > 10) return 'Custom tariff seems too high. Please verify (SAR/kWh)';
    return '';
  };

  // Validate all fields
  const validateForm = () => {
    const errors = {
      monthlyUsage: validateMonthlyConsumptionArray(billData.monthlyConsumptionArray),
      monthlyBill: validateMonthlyBill(billData.monthlyBill || 0),
      utilityProvider: validateUtilityProvider(billData.utilityProvider || ''),
      morningConsumptionPct: validateMorningConsumptionPct(billData.morningConsumptionPct),
      customTariffSarPerKwh: validateCustomTariffSarPerKwh(billData.customTariffSarPerKwh),
    };
    
    setValidationErrors(errors);
    return !Object.values(errors).some(error => error !== '');
  };

  // Create refs for file inputs
  const billFileRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUploading(true);
    setUploadError(null);

    // Validate form before proceeding
    if (!validateForm()) {
      setIsUploading(false);
      return;
    }

    try {
      const monthlyConsumptionArray =
        billData.monthlyConsumptionArray && billData.monthlyConsumptionArray.length === 12
          ? billData.monthlyConsumptionArray
          : new Array(12).fill(0);
      const yearlyConsumption = monthlyConsumptionArray.reduce((sum, v) => sum + (Number.isFinite(v) ? v : 0), 0);
      const derivedMonthlyUsage = yearlyConsumption / 12;
      const morningConsumptionPct = normalizeMorningConsumptionPct(billData.morningConsumptionPct);
      const effectiveYearlyConsumption = yearlyConsumption * (morningConsumptionPct / 100);
      const customTariffSarPerKwh =
        typeof billData.customTariffSarPerKwh === 'number' &&
        Number.isFinite(billData.customTariffSarPerKwh) &&
        billData.customTariffSarPerKwh > 0
          ? billData.customTariffSarPerKwh
          : undefined;

      // Generate a unique session ID for this upload
      const sessionId = crypto.randomUUID();
      const uploadedFiles: Record<string, string> = {};

      // Upload electricity bill if exists
      if (selectedFiles.bill) {
        const fileExt = selectedFiles.bill.name.split('.').pop();
        const fileName = `electricity_bill.${fileExt}`;
        const filePath = `temp/${sessionId}/bills/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('project-files')
          .upload(filePath, selectedFiles.bill, { upsert: true });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('project-files')
          .getPublicUrl(filePath);

        uploadedFiles['electricity_bill'] = publicUrl;
      }


      // Prepare bill data for parent component
      const billDataForDB = {
        electricity_bill: uploadedFiles['electricity_bill'] || null,
        // Keep DB/export compatibility: store average monthly usage derived from the 12-month breakdown
        monthly_usage: derivedMonthlyUsage,
        monthly_bill: billData.monthlyBill || 0,
        custom_tariff_sar_per_kwh: customTariffSarPerKwh,
        utility_provider: billData.utilityProvider || '',
        yearly_consumption: yearlyConsumption,
        morning_consumption_pct: morningConsumptionPct,
        effective_yearly_consumption: effectiveYearlyConsumption
      };

      // Continue to next step with all file URLs and database data
      onNext({
        ...billData,
        type: 'bill',
        uploadedBillUrl: uploadedFiles['electricity_bill'],
        monthlyUsage: derivedMonthlyUsage,
        yearlyConsumption,
        customTariffSarPerKwh,
        morningConsumptionPct,
        effectiveConsumptionFactor: morningConsumptionPct / 100,
        effectiveYearlyConsumption,
        billDataForDB: billDataForDB
      } as BillData);

    } catch (error) {
      console.error('Upload error:', error);
      setUploadError((error as Error).message || 'Failed to upload files. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileUpload = (type: 'bill', files: FileList | null) => {
    if (!files || files.length === 0) return;

    if (type === 'bill') {
      setSelectedFiles(prev => ({ ...prev, bill: files[0] }));
      if (!BILL_OCR_ENABLED) {
        // Keep upload flow working while OCR is temporarily disabled.
        setManualEntry(true);
        setShowOCRPreview(false);
        setExtractedBillInfo(null);
        setOcrFilledFields({});
        return;
      }
      // Trigger OCR processing for bill files when OCR is enabled.
      processBillWithOCR(files[0]);
    }
  };

  const removeFile = (type: 'bill') => {
    if (type === 'bill') {
      setSelectedFiles(prev => ({ ...prev, bill: undefined }));
    }
  };

  const triggerFileInput = (type: 'bill') => {
    if (type === 'bill') {
      billFileRef.current?.click();
    }
  };

  // OCR processing function
  const processBillWithOCR = async (file: File) => {
    setIsProcessingOCR(true);
    setUploadError(null);

    try {
      const result = await analyzeBillWithOCR(file);
      
      if (result.success && result.billInfo) {
        setExtractedBillInfo(result.billInfo);
        setShowOCRPreview(true);
        
        // Auto-fill form with extracted data
        if (result.billInfo?.usage) {
          const monthly = result.billInfo.usage;
          setBillData(prev => ({
            ...prev,
            monthlyConsumptionArray: new Array(12).fill(monthly),
            yearlyConsumption: monthly * 12,
            monthlyUsage: monthly
          }));
        }
        
        // Auto-fill bill amount if available
        if (result.billInfo?.totalAmount) {
          setBillData(prev => ({
            ...prev,
            monthlyBill: result.billInfo!.totalAmount!
          }));
        }
      } else {
        setUploadError(result.error || 'Failed to extract data from bill. Please enter manually.');
      }
    } catch (error) {
      console.error('OCR Error:', error);
      setUploadError(error instanceof Error ? error.message : 'OCR processing failed. Please enter data manually.');
    } finally {
      setIsProcessingOCR(false);
    }
  };

  // Apply extracted data to form
  const applyExtractedData = () => {
    const newOcrFilledFields: { monthlyUsage?: boolean; monthlyBill?: boolean } = {};
    
    if (extractedBillInfo?.usage) {
      const monthly = extractedBillInfo.usage;
      setBillData(prev => ({
        ...prev,
        monthlyConsumptionArray: new Array(12).fill(monthly),
        yearlyConsumption: monthly * 12,
        monthlyUsage: monthly
      }));
      newOcrFilledFields.monthlyUsage = true;
    }
    
    if (extractedBillInfo?.totalAmount) {
      setBillData(prev => ({
        ...prev,
        monthlyBill: extractedBillInfo!.totalAmount!
      }));
      newOcrFilledFields.monthlyBill = true;
    }
    
    setOcrFilledFields(newOcrFilledFields);
    setShowOCRPreview(false);
    // Clear any validation errors for the fields that were filled
    setValidationErrors(prev => ({
      ...prev,
      monthlyUsage: '',
      monthlyBill: ''
    }));
  };

  // Skip OCR and continue with manual entry
  const skipOCR = () => {
    setShowOCRPreview(false);
    setExtractedBillInfo(null);
    setOcrFilledFields({});
  };

  // Utility provider dropdown handlers
  const selectUtilityProvider = (provider: string) => {
    setBillData(prev => ({
      ...prev,
      utilityProvider: provider
    }));
    setShowUtilityDropdown(false);
    // Clear validation error when user selects
    setValidationErrors(prev => ({
      ...prev,
      utilityProvider: ''
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Bill Upload</h2>
              <p className="text-gray-600 mt-1">Upload your electricity bill and energy information</p>
            </div>
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </button>
            )}
          </div>

          {uploadError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-3" />
              <span className="text-red-700">{uploadError}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Bill Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Electricity Bill (PDF, JPG, PNG)
              </label>
              <div 
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
                  selectedFiles.bill ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-blue-500'
                }`}
                onClick={() => !selectedFiles.bill && triggerFileInput('bill')}
              >
                {selectedFiles.bill ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <FileText className="h-6 w-6 text-green-600 mr-3" />
                      <span className="text-green-700">{selectedFiles.bill.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile('bill');
                      }}
                      className="text-red-500 hover:text-red-700 transition-colors"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-600">
                      Click to upload your electricity bill
                    </p>
                  </>
                )}
                <input
                  ref={billFileRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileUpload('bill', e.target.files)}
                />
              </div>
            </div>

            {/* Manual Entry Option - Show when no bill is uploaded and no OCR is processing */}
            {!manualEntry && !selectedFiles.bill && !isProcessingOCR && !showOCRPreview && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-gray-800 font-medium">Don't have a bill to upload?</h3>
                    <p className="text-gray-600 text-sm mt-1">You can enter your energy information manually</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setManualEntry(true);
                    }}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
                  >
                    Enter Manually
                  </button>
                </div>
              </div>
            )}

            {/* OCR Processing Status */}
            {BILL_OCR_ENABLED && isProcessingOCR && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3"></div>
                  <span className="text-blue-700 font-medium">Processing bill with OCR...</span>
                </div>
                <p className="text-blue-600 text-sm mt-1">Extracting data from your bill</p>
              </div>
            )}

            {/* OCR Preview */}
            {BILL_OCR_ENABLED && showOCRPreview && extractedBillInfo && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className="bg-green-100 rounded-full p-2 mr-3">
                      <Eye className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-green-800 font-semibold text-lg">✅ Data Successfully Extracted!</h3>
                      <p className="text-green-600 text-sm">We found the following information in your bill:</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={skipOCR}
                    className="text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                
                <div className="mb-6 space-y-4">
                  <div className="bg-white p-4 rounded-lg border border-green-200 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Total Consumption</label>
                        <p className="font-bold text-green-700 text-xl">
                          {extractedBillInfo.usage ? 
                            `${extractedBillInfo.usage} ${extractedBillInfo.usageUnit || 'kWh'}` : 
                            'Not found'
                          }
                        </p>
                        {extractedBillInfo.usage && (
                          <p className="text-sm text-gray-500 mt-1">
                            This will be used for monthly usage and yearly consumption calculation
                          </p>
                        )}
                      </div>
                      {extractedBillInfo.usage && (
                        <div className="bg-green-100 rounded-full p-2">
                          <Check className="h-4 w-4 text-green-600" />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg border border-green-200 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Bill Amount</label>
                        <p className="font-bold text-green-700 text-xl">
                          {extractedBillInfo.totalAmount ? 
                            `${extractedBillInfo.totalAmount} SAR` : 
                            'Not found'
                          }
                        </p>
                        {extractedBillInfo.totalAmount && (
                          <p className="text-sm text-gray-500 mt-1">
                            This will be used for monthly bill amount
                          </p>
                        )}
                      </div>
                      {extractedBillInfo.totalAmount && (
                        <div className="bg-green-100 rounded-full p-2">
                          <Check className="h-4 w-4 text-green-600" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={applyExtractedData}
                    className="flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium shadow-md"
                  >
                    <Edit3 className="h-4 w-4 mr-2" />
                    Use This Data
                  </button>
                  <button
                    type="button"
                    onClick={() => { skipOCR(); setManualEntry(true); }}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  >
                    Enter Manually
                  </button>
                </div>
              </div>
            )}

            {/* Monthly Consumption Breakdown (required) */}
            {(manualEntry || showOCRPreview || (!showOCRPreview && !isProcessingOCR && ((billData.monthlyBill || 0) > 0 || (billData.utilityProvider || '').length > 0))) && (
              <div className="border-t border-gray-200 pt-6">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Monthly Consumption (Required)</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Enter your consumption for each month (kWh). We’ll calculate your annual total and use this for system sizing and savings.
                  </p>
                </div>
                <MonthlyConsumptionInput
                  initialValues={billData.monthlyConsumptionArray}
                  onValuesChange={(values) => {
                    const yearlyConsumption = values.reduce((sum, v) => sum + (Number.isFinite(v) ? v : 0), 0);
                    setBillData(prev => ({
                      ...prev,
                      monthlyConsumptionArray: values,
                      yearlyConsumption,
                      monthlyUsage: yearlyConsumption / 12
                    }));
                    if (validationErrors.monthlyUsage) {
                      setValidationErrors(prev => ({
                        ...prev,
                        monthlyUsage: ''
                      }));
                    }
                  }}
                />
                {validationErrors.monthlyUsage && (
                  <p className="mt-3 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {validationErrors.monthlyUsage}
                  </p>
                )}
              </div>
            )}

            {/* Monthly Bill - Only show after OCR or if user chooses manual entry */}
            {(manualEntry || showOCRPreview || (!showOCRPreview && !isProcessingOCR && ((billData.monthlyUsage || 0) > 0 || (billData.monthlyBill || 0) > 0))) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monthly Bill Amount (SAR) *
                  {ocrFilledFields.monthlyBill && (
                    <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <Eye className="h-3 w-3 mr-1" />
                      OCR Filled
                    </span>
                  )}
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    required
                    min="0.01"
                    max="100000"
                    className={`block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
                      validationErrors.monthlyBill ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 
                      ocrFilledFields.monthlyBill ? 'border-green-500 bg-green-50' : ''
                    }`}
                    value={billData.monthlyBill}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      setBillData({
                        ...billData,
                        monthlyBill: value
                      });
                      // Clear OCR indicator when user manually edits
                      if (ocrFilledFields.monthlyBill) {
                        setOcrFilledFields(prev => ({
                          ...prev,
                          monthlyBill: false
                        }));
                      }
                      // Clear validation error when user types
                      if (validationErrors.monthlyBill) {
                        setValidationErrors(prev => ({
                          ...prev,
                          monthlyBill: ''
                        }));
                      }
                    }}
                    placeholder="Enter monthly bill amount in SAR"
                  />
                  {ocrFilledFields.monthlyBill && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <Eye className="h-4 w-4 text-green-600" />
                    </div>
                  )}
                </div>
                {validationErrors.monthlyBill && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {validationErrors.monthlyBill}
                  </p>
                )}
              </div>
            )}

            {/* Utility Provider */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Utility Provider *
              </label>
              <div className="relative">
                <button
                  type="button"
                  className={`w-full flex items-center justify-between px-4 py-3 border rounded-lg shadow-sm text-left transition-colors ${
                    validationErrors.utilityProvider
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-blue-500'
                  }`}
                  onClick={() => setShowUtilityDropdown(!showUtilityDropdown)}
                  onMouseEnter={() => setShowUtilityDropdown(true)}
                  onMouseLeave={() => {
                    // Only close if not hovering over the dropdown
                    setTimeout(() => {
                      if (!showUtilityDropdown) {
                        setShowUtilityDropdown(false);
                      }
                    }, 100);
                  }}
                >
                  <span className="text-gray-700">
                    {billData.utilityProvider || 'Select utility provider...'}
                  </span>
                  <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${
                    showUtilityDropdown ? 'transform rotate-180' : ''
                  }`} />
                </button>
                
                {showUtilityDropdown && (
                  <div 
                    className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg"
                    onMouseEnter={() => setShowUtilityDropdown(true)}
                    onMouseLeave={() => setShowUtilityDropdown(false)}
                  >
                    {utilityProviders.map((provider) => (
                      <div
                        key={provider}
                        className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => selectUtilityProvider(provider)}
                        onMouseEnter={() => setShowUtilityDropdown(true)}
                      >
                        <div className={`w-4 h-4 border-2 rounded mr-3 flex items-center justify-center transition-colors ${
                          billData.utilityProvider === provider
                            ? 'bg-blue-600 border-blue-600' 
                            : 'border-gray-300'
                        }`}>
                          {billData.utilityProvider === provider && (
                            <Check className="h-3 w-3 text-white" />
                          )}
                        </div>
                        <span className="text-gray-700">{provider}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {validationErrors.utilityProvider && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {validationErrors.utilityProvider}
                </p>
              )}
            </div>

            {/* (Removed) Average monthly usage input - we now use the 12-month breakdown */}

            {/* Optional morning/daytime consumption factor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Morning Consumption Share (%) <span className="text-gray-500 font-normal">(Optional)</span>
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                className={`block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
                  validationErrors.morningConsumptionPct ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                }`}
                value={billData.morningConsumptionPct ?? ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  const value = raw === '' ? undefined : Number(raw);
                  setBillData((prev) => ({
                    ...prev,
                    morningConsumptionPct: value
                  }));
                  if (validationErrors.morningConsumptionPct) {
                    setValidationErrors((prev) => ({
                      ...prev,
                      morningConsumptionPct: ''
                    }));
                  }
                }}
                placeholder="Leave empty to use 100%"
              />
              <p className="mt-1 text-xs text-gray-500">
                Solar sizing will use this share of annual consumption. Empty means 100%.
              </p>
              {validationErrors.morningConsumptionPct && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {validationErrors.morningConsumptionPct}
                </p>
              )}
            </div>

            {/* Optional tariff override */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Custom Tariff (SAR/kWh) <span className="text-gray-500 font-normal">(Optional override)</span>
              </label>
              <input
                type="number"
                min="0"
                max="10"
                step="0.0001"
                className={`block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${
                  validationErrors.customTariffSarPerKwh ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                }`}
                value={billData.customTariffSarPerKwh ?? ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  const value = raw === '' ? undefined : Number(raw);
                  setBillData((prev) => ({
                    ...prev,
                    customTariffSarPerKwh: value
                  }));
                  if (validationErrors.customTariffSarPerKwh) {
                    setValidationErrors((prev) => ({
                      ...prev,
                      customTariffSarPerKwh: ''
                    }));
                  }
                }}
                placeholder="Example: 0.1800"
              />
              <p className="mt-1 text-xs text-gray-500">
                If provided, this tariff overrides SEC/default tariff assumptions in savings and payback calculations.
              </p>
              {validationErrors.customTariffSarPerKwh && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {validationErrors.customTariffSarPerKwh}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isUploading}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-xl font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isUploading ? 'Uploading...' : 'Continue'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
